<template>
  <div class="min-w-fit">
    <!-- Sidebar backdrop (mobile only) -->
    <div
      class="fixed inset-0 z-40 bg-black/35 dark:bg-gray-900/40 lg:hidden lg:z-auto transition-opacity duration-200"
      :class="sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'"
      aria-hidden="true"
      @click="$emit('close-sidebar')"
    />

    <!-- Sidebar -->
    <div
      id="sidebar"
      ref="sidebar"
      class="user-sidebar flex lg:flex! flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 h-[100dvh] overflow-hidden w-64 lg:w-25 lg:sidebar-expanded:!w-64 2xl:w-64! shrink-0 bg-gray-100 dark:bg-gray-800 p-4 transition-all duration-200 ease-in-out"
      :class="[variant === 'v2' ? 'border-r border-gray-200/90 dark:border-gray-700/60' : 'rounded-r-2xl shadow-sm ring-1 ring-gray-200/80 dark:shadow-xs dark:ring-0', sidebarOpen ? 'translate-x-0' : '-translate-x-64']"
      style="--calendarPadding: 24px; --daySize: 36px; --daysWidth: calc(var(--daySize) * 7);"
    >

      <!-- Sidebar header -->
      <div class="mb-6 pr-3 sm:px-2">
        <!-- Close button -->
        <button
          ref="trigger"
          type="button"
          class="lg:hidden rounded-md p-1 -m-1 text-gray-600 hover:text-gray-900 hover:bg-gray-200/80 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700/50"
          @click.stop="$emit('close-sidebar')"
          aria-controls="sidebar"
          :aria-expanded="sidebarOpen"
        >
          <span class="sr-only">Close sidebar</span>
          <svg class="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
          </svg>
        </button>
        <!-- Logo -->
        <router-link class="block" to="/user/home">
          <img
            :class="sidebarLogoClass"
            :src="sidebarLogoSrc"
            alt="MetalNx"
          />
        </router-link>
      </div>

      <!-- Links (scroll area so header doesn't jump) -->
      <div class="flex-1 overflow-y-auto no-scrollbar ">
        <div class="space-y-8 ">
          <!-- Digital Tools group -->
          <div>
            <h3 class="text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold pl-[var(--calendarPadding)]">
              <span class="hidden lg:block lg:sidebar-expanded:hidden 2xl:hidden text-center w-[var(--daySize)]" aria-hidden="true">•••</span>
              <span class="lg:hidden lg:sidebar-expanded:block 2xl:block pl-8 font-bold">MetalNx Digital Tools</span>
            </h3>
            <ul class="mt-2 mr-4 ">
            <!-- HOME -->
            <template v-if="homeMenuItem">
              
              <router-link :to="homeMenuItem.to" custom v-slot="{ href, navigate }">
                <li
                  class="pl-4 pr-3 py-2 rounded-lg mb-0.5 last:mb-0"
                  :class="isActiveRoute(homeMenuItem.to) && 'us-sidebar-row-active'"
                >
                  <a
                    class="block text-gray-800 dark:text-gray-100 truncate transition"
                    :class="isActiveRoute(homeMenuItem.to) ? '' : 'hover:text-gray-900 dark:hover:text-white'"
                    :href="href"
                    @click="navigate"
                  >
                    <div class="flex items-center">
                      <span
                          class="us-sidebar-icon-wrap"
                          :class="isActiveRoute(homeMenuItem.to) ? 'us-sidebar-icon-wrap--active' : 'us-sidebar-icon-wrap--idle'"
                      >
                        <icon id="menu-home" size="16px" class="shrink-0" />
                      </span>
                      <span
                        class="text-sm ml-4 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200"
                        :class="isActiveRoute(homeMenuItem.to) ? 'us-text-primary font-semibold' : 'text-gray-800 dark:text-white font-medium'"
                      >Home</span>
                    </div>
                  </a>
                </li>
              </router-link>
            </template>

            <!-- Menu Items with Submenus -->
            <SidebarLinkGroup
              v-for="item in menuItemsWithSubmenus"
              :key="item.id"
              v-slot="parentLink"
              :activeCondition="isActiveGroup(item)"
            >
              <a
                class="block text-gray-800 dark:text-gray-100 truncate transition"
                :class="[
                  item.disabled ? 'opacity-50 cursor-not-allowed' : '',
                  isActiveGroup(item) ? '' : 'hover:text-gray-900 dark:hover:text-white'
                ]"
                href="#0"
                @click.prevent="!item.disabled && (parentLink.handleClick(), ensureSidebarExpanded())"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <span
                        class="us-sidebar-icon-wrap"
                        :class="item.disabled ? 'us-sidebar-icon-wrap--idle' : (isActiveGroup(item) ? 'us-sidebar-icon-wrap--active' : 'us-sidebar-icon-wrap--idle')"
                    >
                      <icon :id="item.icon" size="16px" class="shrink-0" />
                    </span>
                    <span
                      class="text-sm ml-4 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200"
                      :class="[
                        isGroupParentLabelPrimary(item) ? 'us-text-primary' : 'text-gray-800 dark:text-white',
                        isActiveGroup(item) ? 'font-semibold' : 'font-medium'
                      ]"
                    >{{ item.name }}</span>
                  </div>
                  <!-- Icon -->
                  <div class="flex shrink-0 ml-13 pl-8">
                    <svg
                        class="w-3 h-3 shrink-0 ml-1 fill-current transition"
                        :class="[
                          parentLink.expanded && 'rotate-180',
                          isActiveGroup(item) ? 'us-sidebar-chevron--active' : 'us-sidebar-chevron--idle'
                        ]"
                        viewBox="0 0 12 12"
                    >
                      <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                    </svg>
                  </div>
                </div>
              </a>
              <div class="lg:hidden lg:sidebar-expanded:block 2xl:block">
                <ul class="pl-8 mt-1" :class="!parentLink.expanded && 'hidden'">
                  <template v-for="subitem in item.sub">
                    <router-link
                      v-if="!subitem.disabled"
                      :key="subitem.id"
                      :to="subitem.to"
                      custom
                      v-slot="{ href, navigate }"
                    >
                      <li class="mb-1 last:mb-0">
                        <a
                          class="block transition truncate"
                          :class="isActiveRoute(subitem.to) ? 'us-text-primary' : 'text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
                          :href="href"
                          @click="navigate"
                        >
                          <span
                            class="text-sm lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200"
                            :class="isActiveRoute(subitem.to) ? 'us-text-primary font-semibold' : 'text-gray-500/90 dark:text-gray-400 font-medium'"
                          >{{ subitem.name }}</span>
                        </a>
                      </li>
                    </router-link>
                    <li v-else :key="subitem.id" class="mb-1 last:mb-0 opacity-50 cursor-not-allowed">
                      <span class="block text-gray-500/90 dark:text-gray-400 truncate">
                        <span class="text-sm font-medium text-gray-800 dark:text-white lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">{{ subitem.name }}</span>
                      </span>
                    </li>
                  </template>
                </ul>
              </div>
            </SidebarLinkGroup>

            <!-- Menu Items without Submenus -->
            <template v-for="item in menuItemsWithoutSubmenus">
              <router-link
                v-if="!item.disabled"
                :key="item.id"
                :to="item.to"
                custom
                v-slot="{ href, navigate }"
              >
                <li
                  class="pl-4 pr-3 py-2 rounded-lg mb-0.5 last:mb-0"
                  :data-tour="item.id === 'I_PREDIX' ? 'predix-menu' : undefined"
                  :class="[
                    isActiveRoute(item.to) && 'us-sidebar-row-active',
                    item.locked && 'us-sidebar-row-locked',
                  ]"
                >
                  <a
                    class="block text-gray-800 dark:text-gray-100 truncate transition"
                    :class="isActiveRoute(item.to) ? '' : 'hover:text-gray-900 dark:hover:text-white'"
                    :href="href"
                    @click="navigate"
                  >
                    <div class="flex items-center">
                      <span
                          class="us-sidebar-icon-wrap"
                          :class="isActiveRoute(item.to) ? 'us-sidebar-icon-wrap--active' : 'us-sidebar-icon-wrap--idle'"
                      >
                        <icon :id="item.icon" size="16px" class="shrink-0" />
                      </span>
                      <span
                        class="text-sm ml-4 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200"
                        :class="isActiveRoute(item.to) ? 'us-text-primary font-semibold' : 'text-gray-800 dark:text-white font-medium'"
                      >{{ item.name }}</span>
                    </div>
                  </a>
                </li>
              </router-link>

              <li v-else :key="item.id" class="pl-4 pr-3 py-2 rounded-lg mb-0.5 last:mb-0 opacity-50 cursor-not-allowed">
                <span class="block text-gray-800 dark:text-gray-100 truncate">
                  <div class="flex items-center">
                    <span class="us-sidebar-icon-wrap us-sidebar-icon-wrap--idle">
                      <icon :id="item.icon" size="16px" class="shrink-0" />
                    </span>
                    <span class="text-sm font-medium ml-4 text-gray-800 dark:text-white lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">{{ item.name }}</span>
                  </div>
                </span>
              </li>
            </template>
          </ul>
          </div>

          <!-- More group -->
          <div>
            <h3 class="text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold pl-[var(--calendarPadding)] mt-3">
              <span class="hidden lg:block lg:sidebar-expanded:hidden 2xl:hidden text-center w-[var(--daySize)]" aria-hidden="true">•••</span>
              <span class="lg:hidden lg:sidebar-expanded:block 2xl:block pl-8 font-bold">More</span>
            </h3>
            <ul class="mt-3">
            <li class="pl-4 pr-3 py-2 mr-4 rounded-lg mb-0.5 last:mb-0">
              <a
                class="block text-gray-800 dark:text-gray-100 truncate transition hover:text-gray-900 dark:hover:text-white"
                href="#0"
                @click.prevent="logout"
              >
                <div class="flex items-center">
                  <span class="us-sidebar-icon-wrap us-sidebar-icon-wrap--idle">
                    <icon id="logout" size="16px" class="shrink-0" />
                  </span>
                  <span class="text-sm font-medium ml-4 text-gray-800 dark:text-white lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">Logout</span>
                </div>
              </a>
            </li>
          </ul>
          </div>
        </div>
      </div>

      <!-- Expand / collapse button -->
      <div class="pt-3 hidden lg:inline-flex 2xl:hidden justify-end">
        <div class="w-12 pl-4 pr-3 py-2">
          <button
            type="button"
            class="rounded-md p-1 -m-1 text-gray-600 hover:text-gray-900 hover:bg-gray-200/80 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700/50"
            @click.prevent="toggleSidebarExpanded"
          >
            <span class="sr-only">Expand / collapse sidebar</span>
            <svg class="shrink-0 fill-current sidebar-expanded:rotate-180" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
              <path d="M15 16a1 1 0 0 1-1-1V1a1 1 0 1 1 2 0v14a1 1 0 0 1-1 1ZM8.586 7H1a1 1 0 1 0 0 2h7.586l-2.793 2.793a1 1 0 1 0 1.414 1.414l4.5-4.5A.997.997 0 0 0 12 8.01M11.924 7.617a.997.997 0 0 0-.217-.324l-4.5-4.5a1 1 0 0 0-1.414 1.414L8.586 7M12 7.99a.996.996 0 0 0-.076-.373Z" />
            </svg>
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon'
import SidebarLinkGroup from './SidebarLinkGroup.vue'
import { platformLogoForUser, platformLogoClassForUser } from '@/utils/platformLogo'

export default {
  name: 'UserSidebar',
  components: {
    Icon,
    SidebarLinkGroup
  },
  props: {
    sidebarOpen: {
      type: Boolean,
      default: false
    },
    menuItems: {
      type: Array,
      required: true
    },
    variant: {
      type: String,
      default: ''
    }
  },
  computed: {
    homeMenuItem() {
      return this.menuItems.find(m => m.id === 'HOME')
    },
    menuItemsWithSubmenus() {
      return this.menuItems.filter(item => item.sub && item.sub.length > 0 && item.id !== 'HOME')
    },
    menuItemsWithoutSubmenus() {
      return this.menuItems.filter(item => (!item.sub || item.sub.length === 0) && item.id !== 'HOME')
    },
    sidebarLogoSrc() {
      return platformLogoForUser(this.$store.state.Username, {
        dark: !!this.$store.state.darkMode,
        context: 'sidebar',
      })
    },
    sidebarLogoClass() {
      return platformLogoClassForUser(this.$store.state.Username, { context: 'sidebar' })
    },
  },
  data() {
    const stored = localStorage.getItem('sidebar-expanded')
    const isExpanded = stored === null ? false : stored === 'true'
    return {
      sidebarExpanded: isExpanded
    }
  },
  methods: {
    toggleSidebarExpanded() {
      this.sidebarExpanded = !this.sidebarExpanded
      localStorage.setItem('sidebar-expanded', this.sidebarExpanded)
      if (this.sidebarExpanded) {
        document.body.classList.add('sidebar-expanded')
      } else {
        document.body.classList.remove('sidebar-expanded')
      }
    },
    ensureSidebarExpanded() {
      if (!this.sidebarExpanded) {
        this.sidebarExpanded = true
        localStorage.setItem('sidebar-expanded', 'true')
        document.body.classList.add('sidebar-expanded')
      }
    },
    isActiveRoute(route) {
      if (!route || !this.$route) return false
      return this.$route.path === route || this.$route.path.startsWith(route + '/')
    },
    isActiveGroup(item) {
      if (!item) return false
      if (this.isActiveRoute(item.to)) return true
      return item.sub && item.sub.length > 0 && item.sub.some(sub => sub && this.isActiveRoute(sub.to))
    },
    /** True when any submenu route matches (parent row icon uses primary; label stays neutral). */
    hasActiveSubmenuItem(item) {
      if (!item || !item.sub || !item.sub.length) return false
      return item.sub.some(sub => sub && !sub.disabled && this.isActiveRoute(sub.to))
    },
    /** Parent category label uses primary only when the group is active but not due to a selected sub-page. */
    isGroupParentLabelPrimary(item) {
      return this.isActiveGroup(item) && !this.hasActiveSubmenuItem(item)
    },
    logout() {
      this.$store.commit('logout')
      this.$router.push('/login')
    },
    clickHandler(e) {
      if (!this.sidebarOpen) return
      const sidebar = this.$refs.sidebar
      const trigger = this.$refs.trigger
      if (sidebar && sidebar.contains(e.target)) return
      if (trigger && trigger.contains(e.target)) return
      this.$emit('close-sidebar')
    },
    keyHandler(e) {
      if (!this.sidebarOpen || e.keyCode !== 27) return
      this.$emit('close-sidebar')
    }
  },
  mounted() {
    if (this.sidebarExpanded) document.body.classList.add('sidebar-expanded')
    document.addEventListener('click', this.clickHandler)
    document.addEventListener('keydown', this.keyHandler)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.clickHandler)
    document.removeEventListener('keydown', this.keyHandler)
  },
  watch: {
    '$route'() {}
  }
}
</script>

<style scoped lang="scss">
/* Match CastingExploreResult.vue: Inter stack via design tokens */
.user-sidebar {
  font-family: var(--font-inter), var(--font-sans);
}

.user-sidebar :where(button, a) {
  font-family: inherit;
}

/* Vuetify theme primary (light/dark) — customProperties on theme */
.user-sidebar .us-text-primary {
  color: var(--v-primary-base) !important;
}

/* Menu icons: primary when row or any child route active; gray when idle (Icon uses fill: currentColor) */
.user-sidebar .us-sidebar-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
}

.user-sidebar .us-sidebar-icon-wrap--active {
  color: var(--v-primary-base) !important;
}

.user-sidebar .us-sidebar-icon-wrap--idle {
  color: rgb(156 163 175) !important;
}

html.dark .user-sidebar .us-sidebar-icon-wrap--idle {
  color: rgb(107 114 128) !important;
}

.user-sidebar .us-sidebar-icon-wrap ::v-deep(svg.icon),
.user-sidebar .us-sidebar-icon-wrap ::v-deep(.icon--mask) {
  fill: currentColor;
  color: inherit;
}

.user-sidebar .us-sidebar-chevron--active {
  color: var(--v-primary-base);
}

.user-sidebar .us-sidebar-chevron--idle {
  color: rgb(156 163 175);
}

html.dark .user-sidebar .us-sidebar-chevron--idle {
  color: rgb(107 114 128);
}

.user-sidebar li.us-sidebar-row-active {
  background-image: linear-gradient(
    to right,
    color-mix(in srgb, var(--v-primary-base) 18%, transparent),
    color-mix(in srgb, var(--v-primary-base) 5%, transparent)
  );
}

html.dark .user-sidebar li.us-sidebar-row-active {
  background-image: linear-gradient(
    to right,
    color-mix(in srgb, var(--v-primary-base) 24%, transparent),
    color-mix(in srgb, var(--v-primary-base) 4%, transparent)
  );
}

.user-sidebar li.us-sidebar-row-locked {
  opacity: 0.55;
}

.user-sidebar li.us-sidebar-row-locked:hover {
  opacity: 0.72;
}

.user-sidebar li.us-sidebar-row-locked.us-sidebar-row-active {
  opacity: 0.85;
}

/* Focus ring for controls (light/dark) — matches primary tint */
.user-sidebar :where(button, a):focus-visible {
  outline: 2px solid color-mix(in srgb, var(--v-primary-base) 55%, transparent);
  outline-offset: 2px;
}

/* Default 48×48 mark */
.us-sidebar-logo {
  display: block;
  height: 2rem;
  width: auto;
  margin: 1.5rem 0 1rem 2rem;
  object-fit: contain;
}

/* Abdulla EGA 200×48 — same height, wider */
.us-sidebar-logo--ega {
  height: 2rem;
  max-width: 8.5rem;
  margin-top: 1.5rem;
  margin-left: 1rem;
}
</style>
